// Journey-wide verification-failure tracking ("Action on Failure").
//
// Two independent thresholds terminate a journey as SUSPECTED_FRAUD:
//   - one GLOBAL cumulative counter  (any real failure, never resets), and
//   - one per-API consecutive counter (resets on that API's own success).
//
// The cardinal rule: an API/network/timeout error must NEVER be counted as a
// verification failure, or a customer on bad network gets flagged as fraud.
// That is why every raw result is first mapped to one of three OUTCOMEs and
// api_error is dropped on the floor by the reducer.
//
// These are pure functions so the counting logic can be unit-tested in
// isolation, the same way participantStats.js is.

export const OUTCOME = {
  PASS: "pass",
  REAL_FAIL: "real_fail",
  API_ERROR: "api_error",
};

const is2xx = (status) =>
  Number.isInteger(status) && status >= 200 && status < 300;

// ---- classifiers: raw API result -> OUTCOME --------------------------------
// Each takes { error, status, body }: `error` is a thrown/aborted request,
// `status` the HTTP status, `body` the parsed JSON. Anything short of a 2xx
// with a well-formed verdict is api_error (not counted).

export function classifyFaceDetect({ error, status, body } = {}) {
  if (error || !is2xx(status)) return OUTCOME.API_ERROR;
  if (!body || typeof body.number_of_faces !== "number") return OUTCOME.API_ERROR;
  return body.number_of_faces === 1 ? OUTCOME.PASS : OUTCOME.REAL_FAIL;
}

export function classifySpoof({ error, status, body } = {}) {
  if (error || !is2xx(status)) return OUTCOME.API_ERROR;
  if (!body || typeof body.spoof_detected !== "boolean") return OUTCOME.API_ERROR;
  return body.spoof_detected ? OUTCOME.REAL_FAIL : OUTCOME.PASS;
}

export function classifyFaceMatch({ error, status, body } = {}) {
  if (error || !is2xx(status)) return OUTCOME.API_ERROR;
  if (!body || typeof body.verified !== "boolean") return OUTCOME.API_ERROR;
  return body.verified ? OUTCOME.PASS : OUTCOME.REAL_FAIL;
}

// ---- configuration: the four REACT_APP_ thresholds -------------------------

const parsePositiveInt = (value, fallback) => {
  const n = parseInt(value, 10);
  return Number.isInteger(n) && n > 0 ? n : fallback;
};

export function getFailureThresholds(env = process.env) {
  return {
    cumulative: parsePositiveInt(env.REACT_APP_CUMULATIVE_FAILURE_THRESHOLD, 10),
    faces: parsePositiveInt(
      env.REACT_APP_FACE_DETECT_CONSECUTIVE_FAIL_THRESHOLD,
      5,
    ),
    spoof: parsePositiveInt(
      env.REACT_APP_SPOOF_DETECT_CONSECUTIVE_FAIL_THRESHOLD,
      5,
    ),
    match: parsePositiveInt(
      env.REACT_APP_FACE_MATCH_CONSECUTIVE_FAILURE_THRESHOLD,
      5,
    ),
  };
}

// ---- the reducer -----------------------------------------------------------

export function initFailureState() {
  return { cumulative: 0, consecutive: { faces: 0, spoof: 0, match: 0 } };
}

// Fold one classified outcome for `apiName` into the counter state. Returns the
// next state plus whether a threshold was breached (and which). Pure — never
// mutates the input.
export function applyOutcome(state, apiName, outcome, thresholds) {
  if (outcome === OUTCOME.API_ERROR) {
    return { state, breached: false, reason: null };
  }

  const consecutive = { ...state.consecutive };
  let cumulative = state.cumulative;

  if (outcome === OUTCOME.PASS) {
    consecutive[apiName] = 0;
  } else if (outcome === OUTCOME.REAL_FAIL) {
    cumulative += 1;
    consecutive[apiName] = (consecutive[apiName] || 0) + 1;
  }

  const nextState = { cumulative, consecutive };

  let breached = false;
  let reason = null;
  if (cumulative >= thresholds.cumulative) {
    breached = true;
    reason = "CUMULATIVE";
  } else if (consecutive[apiName] >= thresholds[apiName]) {
    breached = true;
    reason = `CONSECUTIVE_${apiName.toUpperCase()}`;
  }

  return { state: nextState, breached, reason };
}
